import type { ApolloQueryResult } from '@apollo/client';
import { Grid2, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { object } from 'yup';

import { FormikTextField } from '@components/FormikFields';
import VerifyEmailModalContent from '@components/Modal/Content/VerifyEmailModalContent';
import RoundedButton from '@components/RoundedButton/index';

import { useAdminChangeEmail, useChangeUserEmail } from '@hooks/api/session';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';

import type { GetUserInput, UserModel } from 'generated/types';
import type { GetUserRecordQuery } from 'generated/user.generated';

const messages = defineMessages({
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  unique: {
    id: 'changeEmail.differentEmail',
    defaultMessage: 'New email must be different from current email',
  },
  invalidEmail: {
    id: 'GLOBAL.invalidEmail',
    defaultMessage: 'Invalid email',
  },
  currentEmail: {
    id: 'changeEmail.currentEmail',
    defaultMessage: 'Current Email:',
  },
  newEmail: {
    id: 'changeEmail.newEmail',
    defaultMessage: 'New Email',
  },
  disclaimer: {
    id: 'changeEmail.disclaimer',
    defaultMessage:
      'This email address is used for notifications and as the username for log in',
  },
});

type FormValues = {
  email: string;
};

interface ChangeEmailProps {
  user?: Pick<UserModel, 'email' | 'user_id'>;
  refetch: (
    variables?:
      | {
          input: GetUserInput;
        }
      | undefined
  ) => Promise<ApolloQueryResult<GetUserRecordQuery>>;
}

const ChangeEmail: React.FC<ChangeEmailProps> = ({ user, refetch }) => {
  const { formatMessage } = useIntl();
  const [changeEmail] = useChangeUserEmail();
  const [adminChangeEmail] = useAdminChangeEmail();
  const { emailSchema } = useCommonSchemas();
  const { closeModal, openModal, setContent } = useModal();
  const {
    state: { activeProfile, loggedInProfile },
  } = useSession();
  const isEditingSelf =
    !!activeProfile?.user_id &&
    activeProfile.user_id === loggedInProfile?.user_id;

  const validationSchema = object().shape({
    email: emailSchema
      .required(formatMessage(messages.requiredText))
      .notOneOf([user?.email], formatMessage(messages.unique)),
  });

  const handleOpenModal = (medium: string, destination: string) => {
    openModal({
      width: 400,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: (
        <VerifyEmailModalContent
          medium={medium}
          destination={destination}
          onSubmit={async () => {
            await refetch({ input: { user_id: user?.user_id ?? '' } });
            closeModal();
          }}
        />
      ),
    });
  };

  async function handleSubmit(values: FormValues) {
    if (!user?.email) {
      throw new Error('Current user has no email');
    }

    if (!isEditingSelf) {
      const { data } = await adminChangeEmail({
        input: {
          currentEmail: user.email,
          newEmail: values.email,
        },
      });
      if (data?.adminChangeEmail) {
        await refetch({ input: { user_id: user.user_id } });
      }
      return;
    }

    const { data } = await changeEmail({
      input: {
        newEmail: values.email,
      },
    });
    if (data?.changeUserEmail) {
      const delivery = data.changeUserEmail.CodeDeliveryDetailsList?.at(0);
      if (!delivery?.DeliveryMedium || !delivery.Destination) {
        throw new Error('Failed to change user email');
      }
      handleOpenModal(delivery.DeliveryMedium, delivery.Destination);
    }
  }

  const initialValues: FormValues = {
    email: '',
  };

  return (
    <Grid2 container>
      <Grid2 size={{ xs: 12 }}>
        <Stack direction="row" spacing={1}>
          <Typography color="primary">
            {formatMessage(messages.currentEmail)}
          </Typography>
          <Typography>{user?.email}</Typography>
        </Stack>
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        <Typography color="text.secondary" variant="body2">
          {formatMessage(messages.disclaimer)}
        </Typography>
      </Grid2>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => (
          <Form autoComplete="off">
            <FormikTextField
              autoComplete="off"
              name="email"
              label={formatMessage(messages.newEmail)}
              required
              type="email"
            />
            <RoundedButton disabled={isSubmitting} type="submit">
              {formatMessage(messages.submit)}
            </RoundedButton>
          </Form>
        )}
      </Formik>
    </Grid2>
  );
};

export default ChangeEmail;
